import { useState, useEffect, useCallback, useRef } from 'react';

const API_BASE = import.meta.env.VITE_API_URL || '';

// Прямые запросы без Telegram-авторизации
async function adminApi(path: string, opts: RequestInit = {}) {
  const token = localStorage.getItem('admin_token');
  const headers: Record<string, string> = {
    'X-Admin-Password': token || '',
    ...(opts.headers as Record<string, string>),
  };
  if (opts.body) {
    headers['Content-Type'] = 'application/json';
  }
  const res = await fetch(`${API_BASE}/api/admin${path}`, {
    ...opts,
    headers,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || `HTTP ${res.status}`);
  }
  return res.json();
}

// ===== Экран логина =====
function LoginScreen({ onLogin }: { onLogin: () => void }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      const data = await res.json();
      if (data.ok) {
        localStorage.setItem('admin_token', data.token);
        onLogin();
      } else {
        setError(data.error || 'Ошибка');
      }
    } catch {
      setError('Ошибка подключения');
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0c0c18' }}>
      <form onSubmit={handleSubmit} style={{
        background: '#151525', borderRadius: 16, padding: 32, width: 340,
        border: '1px solid rgba(255,255,255,0.08)',
      }}>
        <h1 style={{ color: '#fff', fontSize: 20, fontWeight: 600, marginBottom: 24, textAlign: 'center' }}>
          2EnBota Admin
        </h1>
        <input
          type="password"
          value={password}
          onChange={e => setPassword(e.target.value)}
          placeholder="Пароль"
          autoFocus
          style={{
            width: '100%', padding: '12px 14px', background: '#1a1a2e', border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 10, color: '#fff', fontSize: 14, marginBottom: 16, outline: 'none', boxSizing: 'border-box',
          }}
        />
        {error && <div style={{ color: '#ff5252', fontSize: 13, marginBottom: 12 }}>{error}</div>}
        <button type="submit" style={{
          width: '100%', padding: '12px', background: '#8B5CF6', color: '#fff',
          border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer',
        }}>
          Войти
        </button>
      </form>
    </div>
  );
}

// ===== Ударение =====
const IPA_VOWELS = new Set('aɑæʌɒɔəɛɜɪiɵʊuːeɝoʉ');
const EN_VOWELS = new Set('aeiouyAEIOUY');
const RU_VOWELS = new Set('аеёиоуыэюяАЕЁИОУЫЭЮЯ');

function getStressVowelIndex(word: string, ipa: string, vowelSet: Set<string>): number {
  if (!ipa || !word) return -1;
  const stressPos = ipa.indexOf('ˈ');
  if (stressPos === -1) return -1;
  let ipaVowelIdx = 0;
  for (let i = 0; i < stressPos; i++) {
    if (IPA_VOWELS.has(ipa[i])) ipaVowelIdx++;
  }
  let count = 0;
  for (let i = 0; i < word.length; i++) {
    if (vowelSet.has(word[i])) {
      if (count === ipaVowelIdx) return i;
      count++;
    }
  }
  return -1;
}

function withStress(word: string, ipa: string, vowelSet: Set<string>): string {
  const idx = getStressVowelIndex(word, ipa, vowelSet);
  if (idx < 0) return word;
  return word.slice(0, idx + 1) + '\u0301' + word.slice(idx + 1);
}

// Озвучка через браузер
function speakWord(text: string) {
  if ('speechSynthesis' in window) {
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.lang = 'en-US';
    u.rate = 0.9;
    window.speechSynthesis.speak(u);
  }
}

// Рендер предложения с подсветкой изучаемого слова
function HighlightedWordSentence({ sentence, targetWord }: { sentence: string; targetWord: string }) {
  if (!sentence) return <span>—</span>;
  if (!targetWord) return <span>{sentence}</span>;

  const escapeRegex = (s: string) => s.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
  const baseTarget = targetWord.trim().toLowerCase();
  
  // Создаем регулярку для поиска слова или его основы
  const regex = new RegExp(`(\\b${escapeRegex(targetWord)}\\b|\\b${escapeRegex(baseTarget)}\\w*)`, 'gi');
  const parts = sentence.split(regex);

  return (
    <span>
      {parts.map((p, idx) => {
        if (p && regex.test(p)) {
          return (
            <span key={idx} style={{
              color: '#c084fc', fontWeight: 700,
              background: 'rgba(139,92,246,0.15)', padding: '0 3px', borderRadius: 3,
            }}>
              {p}
            </span>
          );
        }
        return <span key={idx}>{p}</span>;
      })}
    </span>
  );
}

const CEFR_CONFIG: Record<string, { label: string; bg: string; activeBg: string; color: string; border: string; glow: string }> = {
  A1: { label: 'A1 Beginner', bg: 'rgba(34, 197, 94, 0.1)', activeBg: 'rgba(34, 197, 94, 0.28)', color: '#4ade80', border: 'rgba(34, 197, 94, 0.35)', glow: '0 0 14px rgba(74, 222, 128, 0.35)' },
  A2: { label: 'A2 Elementary', bg: 'rgba(16, 185, 129, 0.1)', activeBg: 'rgba(16, 185, 129, 0.28)', color: '#34d399', border: 'rgba(16, 185, 129, 0.35)', glow: '0 0 14px rgba(52, 211, 153, 0.35)' },
  B1: { label: 'B1 Intermediate', bg: 'rgba(59, 130, 246, 0.1)', activeBg: 'rgba(59, 130, 246, 0.28)', color: '#60a5fa', border: 'rgba(59, 130, 246, 0.35)', glow: '0 0 14px rgba(96, 165, 250, 0.35)' },
  B2: { label: 'B2 Upper-Int', bg: 'rgba(139, 92, 246, 0.1)', activeBg: 'rgba(139, 92, 246, 0.28)', color: '#c084fc', border: 'rgba(139, 92, 246, 0.35)', glow: '0 0 14px rgba(192, 132, 252, 0.35)' },
  C1: { label: 'C1 Advanced', bg: 'rgba(244, 63, 94, 0.1)', activeBg: 'rgba(244, 63, 94, 0.28)', color: '#fb7185', border: 'rgba(244, 63, 94, 0.35)', glow: '0 0 14px rgba(251, 113, 133, 0.35)' },
  C2: { label: 'C2 Proficiency', bg: 'rgba(225, 29, 72, 0.1)', activeBg: 'rgba(225, 29, 72, 0.28)', color: '#f43f5e', border: 'rgba(225, 29, 72, 0.35)', glow: '0 0 14px rgba(244, 63, 94, 0.35)' },
};

// ===== Боковая шторка редактирования слова (Drawer) =====
interface WordDrawerProps {
  word: any;
  onClose: () => void;
  onSaved: () => void;
}

function WordDrawer({ word, onClose, onSaved }: WordDrawerProps) {
  const [formData, setFormData] = useState({
    original_text: word.original_text || '',
    main_translation_text: word.main_translation_text || '',
    alt_translations: (word.translations || []).map((t: any) => t.translation_text).join(', '),
    plural_form: word.plural_form || '',
    part_of_speech: word.part_of_speech || 'noun',
    cefr_level: word.cefr_level || 'B1',
    transcription: word.transcription || '',
    russian_phonetic: word.russian_phonetic || '',
    examples: (word.examples || []).map((ex: any) => ({
      id: ex.id,
      sentence_en: ex.sentence_en || '',
      sentence_en_blanked: ex.sentence_en_blanked || '',
      sentence_ru: ex.sentence_ru || '',
      sentence_ru_blanked: ex.sentence_ru_blanked || '',
    })),
  });

  const [saving, setSaving] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // Гарантируем 3 карточки примеров
  while (formData.examples.length < 3) {
    formData.examples.push({
      id: undefined,
      sentence_en: '',
      sentence_en_blanked: '',
      sentence_ru: '',
      sentence_ru_blanked: '',
    });
  }

  const handleSave = async () => {
    setSaving(true);
    setErrorMsg('');
    setSuccessMsg('');
    try {
      await adminApi(`/dictionary/words/${word.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          original_text: formData.original_text,
          main_translation_text: formData.main_translation_text,
          alt_translations: formData.alt_translations.split(',').map((s: string) => s.trim()).filter(Boolean),
          plural_form: formData.plural_form,
          part_of_speech: formData.part_of_speech,
          cefr_level: formData.cefr_level,
          transcription: formData.transcription,
          russian_phonetic: formData.russian_phonetic,
          examples: formData.examples.filter((e: any) => e.sentence_en.trim() || e.sentence_ru.trim()),
        }),
      });
      setSuccessMsg('✅ Успешно сохранено!');
      setTimeout(() => {
        onSaved();
      }, 600);
    } catch (err: any) {
      setErrorMsg(`❌ ${err.message}`);
    }
    setSaving(false);
  };

  const handleGenerateAiExamples = async () => {
    setAiGenerating(true);
    setErrorMsg('');
    try {
      const res = await adminApi('/dictionary/generate-examples', {
        method: 'POST',
        body: JSON.stringify({
          word: formData.original_text,
          translation: formData.main_translation_text,
          cefr: formData.cefr_level,
          pos: formData.part_of_speech,
        }),
      });
      if (res.ok && res.examples && res.examples.length > 0) {
        setFormData(prev => ({
          ...prev,
          examples: res.examples,
        }));
        setSuccessMsg('✨ 3 примера успешно сгенерированы через AI!');
      } else {
        setErrorMsg(res.error || 'Не удалось сгенерировать примеры');
      }
    } catch (err: any) {
      setErrorMsg(`Ошибка AI: ${err.message}`);
    }
    setAiGenerating(false);
  };

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '9px 12px', background: '#16162a',
    border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8,
    color: '#fff', fontSize: 13, outline: 'none', boxSizing: 'border-box',
  };

  const labelStyle: React.CSSProperties = {
    fontSize: 12, color: '#94a3b8', fontWeight: 600, marginBottom: 4, display: 'block',
  };

  const cefrCfg = CEFR_CONFIG[formData.cefr_level] || { color: '#888', bg: '#222', border: '#444' };

  return (
    <div style={{
      position: 'fixed', top: 0, right: 0, bottom: 0, width: 620, maxWidth: '100vw',
      background: '#0e0e1c', borderLeft: '1px solid rgba(255,255,255,0.12)',
      boxShadow: '-10px 0 30px rgba(0,0,0,0.6)', zIndex: 1000,
      display: 'flex', flexDirection: 'column',
      animation: 'slideIn 0.2s ease-out',
    }}>
      {/* Шапка шторки */}
      <div style={{
        padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.08)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        background: '#141428',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 20, fontWeight: 700, color: '#fff' }}>{formData.original_text}</span>
          <span style={{
            padding: '2px 8px', borderRadius: 6, fontSize: 12, fontWeight: 700,
            background: cefrCfg.bg, color: cefrCfg.color, border: `1px solid ${cefrCfg.border}`,
          }}>
            {formData.cefr_level}
          </span>
          <button
            onClick={() => speakWord(formData.original_text)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16 }}
            title="Озвучить"
          >
            🔊
          </button>
        </div>
        <button
          onClick={onClose}
          style={{
            background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: 8,
            color: '#aaa', width: 32, height: 32, cursor: 'pointer', fontSize: 16,
          }}
        >
          ✕
        </button>
      </div>

      {/* Контент шторки со скроллом */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
        {errorMsg && (
          <div style={{ padding: '10px 14px', background: 'rgba(239,68,68,0.15)', border: '1px solid #ef4444', color: '#fca5a5', borderRadius: 8, marginBottom: 16, fontSize: 13 }}>
            {errorMsg}
          </div>
        )}
        {successMsg && (
          <div style={{ padding: '10px 14px', background: 'rgba(34,197,94,0.15)', border: '1px solid #22c55e', color: '#86efac', borderRadius: 8, marginBottom: 16, fontSize: 13 }}>
            {successMsg}
          </div>
        )}

        {/* Основные данные слова */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
          <div>
            <label style={labelStyle}>Слово (EN)</label>
            <input
              value={formData.original_text}
              onChange={e => setFormData({ ...formData, original_text: e.target.value })}
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Главный перевод (RU)</label>
            <input
              value={formData.main_translation_text}
              onChange={e => setFormData({ ...formData, main_translation_text: e.target.value })}
              style={inputStyle}
            />
          </div>
          <div style={{ gridColumn: 'span 2' }}>
            <label style={labelStyle}>Альтернативные переводы (через запятую)</label>
            <input
              value={formData.alt_translations}
              onChange={e => setFormData({ ...formData, alt_translations: e.target.value })}
              style={inputStyle}
              placeholder="например: стейкхолдер, держатель акций, участник проекта"
            />
          </div>
          <div>
            <label style={labelStyle}>Множественное число (для сущ.)</label>
            <input
              value={formData.plural_form}
              onChange={e => setFormData({ ...formData, plural_form: e.target.value })}
              style={inputStyle}
              placeholder="например: stakeholders"
            />
          </div>
          <div>
            <label style={labelStyle}>Часть речи</label>
            <select
              value={formData.part_of_speech}
              onChange={e => setFormData({ ...formData, part_of_speech: e.target.value })}
              style={inputStyle}
            >
              <option value="noun" style={{ background: '#16162a', color: '#ffffff' }}>noun (существительное)</option>
              <option value="verb" style={{ background: '#16162a', color: '#ffffff' }}>verb (глагол)</option>
              <option value="adjective" style={{ background: '#16162a', color: '#ffffff' }}>adjective (прилагательное)</option>
              <option value="adverb" style={{ background: '#16162a', color: '#ffffff' }}>adverb (наречие)</option>
              <option value="phrase" style={{ background: '#16162a', color: '#ffffff' }}>phrase (фраза / идиома)</option>
              <option value="preposition" style={{ background: '#16162a', color: '#ffffff' }}>preposition (предлог)</option>
              <option value="pronoun" style={{ background: '#16162a', color: '#ffffff' }}>pronoun (местоимение)</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>Уровень CEFR</label>
            <select
              value={formData.cefr_level}
              onChange={e => setFormData({ ...formData, cefr_level: e.target.value })}
              style={inputStyle}
            >
              {['A1', 'A2', 'B1', 'B2', 'C1', 'C2'].map(l => (
                <option key={l} value={l} style={{ background: '#16162a', color: '#ffffff' }}>{l}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={labelStyle}>IPA Транскрипция</label>
            <input
              value={formData.transcription}
              onChange={e => setFormData({ ...formData, transcription: e.target.value })}
              style={inputStyle}
              placeholder="/ˈsteɪkˌhoʊldər/"
            />
          </div>
          <div>
            <label style={labelStyle}>Русская фонетика</label>
            <input
              value={formData.russian_phonetic}
              onChange={e => setFormData({ ...formData, russian_phonetic: e.target.value })}
              style={inputStyle}
              placeholder="стэ́йкхолдер"
            />
          </div>
        </div>

        {/* Секция примеров */}
        <div style={{ marginTop: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: '#fff', margin: 0 }}>
              📚 3 Примера предложений
            </h3>
            <button
              onClick={handleGenerateAiExamples}
              disabled={aiGenerating}
              style={{
                padding: '6px 14px', background: aiGenerating ? '#444' : 'linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)',
                border: 'none', borderRadius: 8, color: '#fff', fontSize: 12, fontWeight: 600, cursor: aiGenerating ? 'default' : 'pointer',
                display: 'flex', alignItems: 'center', gap: 6,
              }}
            >
              <span>{aiGenerating ? '⏳ Генерирую...' : '✨ Сгенерировать через AI'}</span>
            </button>
          </div>

          {formData.examples.map((ex: any, idx: number) => (
            <div key={idx} style={{
              background: '#15152a', border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: 10, padding: 14, marginBottom: 12,
            }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#c084fc', marginBottom: 8 }}>
                Пример #{idx + 1}
              </div>

              <div style={{ marginBottom: 8 }}>
                <label style={labelStyle}>Английский (EN)</label>
                <input
                  value={ex.sentence_en}
                  onChange={e => {
                    const newEx = [...formData.examples];
                    newEx[idx].sentence_en = e.target.value;
                    if (!newEx[idx].sentence_en_blanked) {
                      newEx[idx].sentence_en_blanked = e.target.value.replace(formData.original_text, '_______');
                    }
                    setFormData({ ...formData, examples: newEx });
                  }}
                  style={inputStyle}
                  placeholder="Полное предложение на английском"
                />
              </div>

              <div style={{ marginBottom: 8 }}>
                <label style={labelStyle}>Русский перевод (RU)</label>
                <input
                  value={ex.sentence_ru}
                  onChange={e => {
                    const newEx = [...formData.examples];
                    newEx[idx].sentence_ru = e.target.value;
                    if (!newEx[idx].sentence_ru_blanked) {
                      newEx[idx].sentence_ru_blanked = e.target.value.replace(formData.main_translation_text, '_______');
                    }
                    setFormData({ ...formData, examples: newEx });
                  }}
                  style={inputStyle}
                  placeholder="Перевод на русском"
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <div>
                  <label style={{ ...labelStyle, fontSize: 11 }}>Маска EN (_______)</label>
                  <input
                    value={ex.sentence_en_blanked}
                    onChange={e => {
                      const newEx = [...formData.examples];
                      newEx[idx].sentence_en_blanked = e.target.value;
                      setFormData({ ...formData, examples: newEx });
                    }}
                    style={{ ...inputStyle, fontSize: 12, color: '#94a3b8' }}
                  />
                </div>
                <div>
                  <label style={{ ...labelStyle, fontSize: 11 }}>Маска RU (_______)</label>
                  <input
                    value={ex.sentence_ru_blanked}
                    onChange={e => {
                      const newEx = [...formData.examples];
                      newEx[idx].sentence_ru_blanked = e.target.value;
                      setFormData({ ...formData, examples: newEx });
                    }}
                    style={{ ...inputStyle, fontSize: 12, color: '#94a3b8' }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Подвал шторки */}
      <div style={{
        padding: '14px 20px', borderTop: '1px solid rgba(255,255,255,0.08)',
        display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10,
        background: '#141428',
      }}>
        <button
          onClick={onClose}
          style={{
            padding: '10px 18px', background: 'rgba(255,255,255,0.08)',
            border: 'none', borderRadius: 8, color: '#ccc', fontSize: 13, cursor: 'pointer',
          }}
        >
          Отмена
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            padding: '10px 24px', background: '#8B5CF6',
            border: 'none', borderRadius: 8, color: '#fff', fontSize: 13, fontWeight: 700,
            cursor: saving ? 'default' : 'pointer',
          }}
        >
          {saving ? 'Сохранение...' : '💾 Сохранить слово'}
        </button>
      </div>
    </div>
  );
}

// ===== Главная таблица словаря =====
export function DictionaryEditor() {
  const [words, setWords] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [exact, setExact] = useState(false);
  const [offset, setOffset] = useState(0);
  const [cefr, setCefr] = useState('');
  const [pos, setPos] = useState('');
  const [health, setHealth] = useState('');
  const [hasAlts, setHasAlts] = useState('');
  const [stats, setStats] = useState<any>(null);
  const [syncing, setSyncing] = useState(false);
  const [selectedWord, setSelectedWord] = useState<any | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const limit = 30;

  const updateSearch = useCallback((newVal: string | null | undefined) => {
    const clean = !newVal || newVal === 'null' || newVal === 'undefined' ? '' : String(newVal);
    setSearch(clean);
    setOffset(0);
    if (searchInputRef.current && searchInputRef.current.value !== clean) {
      searchInputRef.current.value = clean;
    }
  }, []);

  useEffect(() => {
    const handleGlobalEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || e.keyCode === 27) {
        if (document.activeElement === searchInputRef.current || search) {
          e.preventDefault();
          e.stopPropagation();
          updateSearch('');
        }
      }
    };
    window.addEventListener('keydown', handleGlobalEsc, { capture: true });
    return () => window.removeEventListener('keydown', handleGlobalEsc, { capture: true });
  }, [search, updateSearch]);

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
      if (search) params.set('q', search);
      if (exact) params.set('exact', 'true');
      if (cefr) params.set('cefr', cefr);
      if (pos) params.set('pos', pos);
      if (health) params.set('health', health);
      if (hasAlts) params.set('has_alts', hasAlts);
      const data = await adminApi(`/dictionary/words?${params}`);
      setWords(data.words);
      setTotal(data.total);
    } catch (err: any) {
      alert(err.message);
    }
  }, [search, exact, offset, cefr, pos, health, hasAlts]);

  useEffect(() => { load(); }, [load]);

  const loadStats = useCallback(async () => {
    try {
      const data = await adminApi('/dictionary/stats');
      setStats(data);
    } catch (err: any) {
      console.error('Stats error:', err.message);
    }
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);

  const [enriching, setEnriching] = useState(false);
  const [enrichStatus, setEnrichStatus] = useState<string | null>(null);

  const syncAlts = async () => {
    if (!confirm('Синхронизировать переводы, синонимы, части речи, CEFR и ударения со всеми пользователями?')) return;
    setSyncing(true);
    try {
      const data = await adminApi('/dictionary/sync-alts', { method: 'POST', body: JSON.stringify({}) });
      alert(`Синхронизация завершена! Обновлено: ${data.synced}, пропущено: ${data.skipped} из ${data.total}`);
    } catch (err: any) {
      alert('Ошибка: ' + err.message);
    }
    setSyncing(false);
  };

  const enrichUnlinked = async () => {
    if (!confirm('Обогатить все кастомные слова пользователей до Золотого Стандарта (часть речи, CEFR, транскрипция, русское ударение, 3 примера и чистый перевод)?')) return;
    setEnriching(true);
    setEnrichStatus('Запуск...');
    try {
      let totalEnriched = 0;
      while (true) {
        const res = await adminApi('/dictionary/enrich-unlinked-words', {
          method: 'POST',
          body: JSON.stringify({ batch_size: 20 }),
        });
        if (!res || res.processed === 0) {
          break;
        }
        totalEnriched += res.processed;
        setEnrichStatus(`Обогащено ${totalEnriched} (осталось ${res.remaining})...`);
        if (res.remaining === 0) break;
      }
      alert(`🎉 Успешно! Обогащено и добавлено в Золотой стандарт: ${totalEnriched} слов!`);
      load();
      loadStats();
    } catch (err: any) {
      alert('Ошибка при обогащении: ' + err.message);
    }
    setEnriching(false);
    setEnrichStatus(null);
  };

  const deleteWord = async (id: number, word: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!confirm(`Удалить "${word}" и все его примеры?`)) return;
    try {
      await adminApi(`/dictionary/words/${id}`, { method: 'DELETE' });
      setWords(prev => prev.filter(w => w.id !== id));
      if (selectedWord?.id === id) setSelectedWord(null);
    } catch (err: any) {
      alert(err.message);
    }
  };

  const totalPages = Math.ceil(total / limit);
  const page = Math.floor(offset / limit) + 1;

  const cellStyle: React.CSSProperties = { padding: '8px 10px', borderBottom: '1px solid rgba(255,255,255,0.05)', fontSize: 13, color: '#ddd', verticalAlign: 'top' };
  const headerStyle: React.CSSProperties = {
    ...cellStyle,
    color: '#a78bfa',
    fontWeight: 700,
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    position: 'sticky',
    top: 0,
    zIndex: 20,
    background: '#15152a',
    borderBottom: '2px solid rgba(139,92,246,0.4)',
    boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
    whiteSpace: 'nowrap',
    userSelect: 'none',
  };

  return (
    <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Панель фильтров */}
      <div style={{
        flexShrink: 0,
        background: '#0f0f1a',
        padding: '12px 18px 10px 18px',
        borderRadius: 10,
        border: '1px solid rgba(255,255,255,0.08)',
        marginBottom: 10,
        boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
      }}>
        {/* 2. Интерактивные чипы уровней CEFR */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <button
            onClick={() => { setCefr(''); setOffset(0); }}
            style={{
              padding: '7px 14px', borderRadius: 8,
              border: cefr === '' ? '1.5px solid #a855f7' : '1px solid rgba(255,255,255,0.12)',
              background: cefr === '' ? 'rgba(168, 85, 247, 0.25)' : '#16162a',
              color: '#ffffff', fontSize: 13, fontWeight: 700, cursor: 'pointer',
              boxShadow: cefr === '' ? '0 0 14px rgba(168, 85, 247, 0.35)' : 'none',
              display: 'flex', alignItems: 'center', gap: 8,
              transition: 'all 0.15s ease',
            }}
          >
            <span>🌐 Все</span>
            <span style={{
              background: cefr === '' ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.08)',
              color: cefr === '' ? '#ffffff' : '#94a3b8',
              padding: '1px 7px', borderRadius: 10, fontSize: 11, fontWeight: 600,
              border: cefr === '' ? '1px solid #a855f7' : '1px solid transparent',
            }}>
              {stats?.total || 0}
            </span>
          </button>
          {['A1', 'A2', 'B1', 'B2', 'C1', 'C2'].map(lvl => {
            const cfg = CEFR_CONFIG[lvl];
            const isSelected = cefr === lvl;
            const count = stats?.cefr_counts?.[lvl] || 0;
            return (
              <button
                key={lvl}
                onClick={() => { setCefr(isSelected ? '' : lvl); setOffset(0); }}
                style={{
                  padding: '7px 14px', borderRadius: 8,
                  border: isSelected ? `1.5px solid ${cfg.color}` : `1px solid ${cfg.border}`,
                  background: isSelected ? cfg.activeBg : cfg.bg,
                  color: '#ffffff',
                  fontSize: 13, fontWeight: 700, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 8,
                  boxShadow: isSelected ? cfg.glow : 'none',
                  transition: 'all 0.15s ease',
                }}
              >
                <span style={{ color: cfg.color }}>{lvl}</span>
                <span style={{
                  background: isSelected ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.08)',
                  color: isSelected ? '#ffffff' : '#94a3b8',
                  padding: '1px 7px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                  border: isSelected ? `1px solid ${cfg.color}` : '1px solid transparent',
                }}>
                  {count}
                </span>
              </button>
            );
          })}

          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              onClick={enrichUnlinked}
              disabled={enriching || syncing}
              title="Находит все кастомные слова пользователей и приводит их к Золотому стандарту (фонетика, CEFR, POS, примеры, исправление опечаток)"
              style={{
                padding: '6px 14px',
                background: enriching ? '#555' : 'rgba(234, 179, 8, 0.2)',
                border: '1px solid rgba(234, 179, 8, 0.4)',
                borderRadius: 8,
                color: '#facc15',
                fontSize: 12,
                fontWeight: 600,
                cursor: enriching ? 'default' : 'pointer',
              }}
            >
              {enriching ? (enrichStatus || 'Обогащение...') : '✨ Обогатить кастомные слова'}
            </button>

            <button
              onClick={syncAlts}
              disabled={syncing || enriching}
              title="Обновляет переводы, синонимы, части речи, CEFR, транскрипции и ударения в карточках всех пользователей"
              style={{
                padding: '6px 14px', background: syncing ? '#555' : 'rgba(139,92,246,0.2)',
                border: '1px solid rgba(139,92,246,0.4)', borderRadius: 8, color: '#c084fc', fontSize: 12, fontWeight: 600, cursor: syncing ? 'default' : 'pointer',
              }}
            >
              {syncing ? 'Синхронизация...' : '🔄 Синхронизировать словарь с юзерами'}
            </button>
          </div>
        </div>

        {/* 3 & 6. Панель фильтров */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: 260, display: 'flex', alignItems: 'center' }}>
            <input
              ref={searchInputRef}
              type="text"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              value={search || ''}
              onChange={e => updateSearch(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Escape' || e.keyCode === 27) {
                  e.preventDefault();
                  e.stopPropagation();
                  updateSearch('');
                }
              }}
              onKeyUp={e => {
                if (e.key === 'Escape' || e.keyCode === 27) {
                  e.preventDefault();
                  e.stopPropagation();
                  updateSearch('');
                }
              }}
              placeholder="Поиск по слову, переводу или альтернативам..."
              style={{
                width: '100%',
                height: 35,
                padding: search ? '0 48px 0 12px' : '0 12px',
                background: '#16162a',
                border: '1px solid rgba(255,255,255,0.15)',
                borderRadius: 8,
                color: '#fff',
                fontSize: 13,
                outline: 'none',
                boxSizing: 'border-box',
                transition: 'all 0.15s ease',
              }}
              onFocus={e => e.currentTarget.style.borderColor = '#8B5CF6'}
              onBlur={e => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)'}
            />
            {search && (
              <div style={{
                position: 'absolute',
                right: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                display: 'flex',
                alignItems: 'center',
                gap: 7,
              }}>
                <div style={{ height: 14, width: 1, background: 'rgba(255,255,255,0.15)' }} />
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    updateSearch('');
                    searchInputRef.current?.focus();
                  }}
                  title="Очистить поиск (Esc)"
                  style={{
                    width: 22,
                    height: 22,
                    minHeight: 'unset',
                    maxHeight: 22,
                    minWidth: 'unset',
                    boxSizing: 'border-box',
                    borderRadius: 6,
                    background: 'rgba(139,92,246,0.18)',
                    border: '1px solid rgba(139,92,246,0.35)',
                    color: '#c084fc',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 0,
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = '#8B5CF6';
                    e.currentTarget.style.color = '#ffffff';
                    e.currentTarget.style.boxShadow = '0 0 10px rgba(139,92,246,0.6)';
                    e.currentTarget.style.transform = 'scale(1.08)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = 'rgba(139,92,246,0.18)';
                    e.currentTarget.style.color = '#c084fc';
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                >
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
            )}
          </div>
          <label
            title="Искать строго полное слово целиком (без похожих и вхождений)"
            style={{
              display: 'flex', alignItems: 'center', gap: 6, color: '#bbb', fontSize: 13,
              cursor: 'pointer', userSelect: 'none', background: '#16162a', padding: '0 12px',
              borderRadius: 8, border: '1px solid rgba(255,255,255,0.15)', height: 35, boxSizing: 'border-box'
            }}
          >
            <input
              type="checkbox"
              checked={exact}
              onChange={e => { setExact(e.target.checked); setOffset(0); }}
              style={{ cursor: 'pointer', accentColor: '#8B5CF6' }}
            />
            Точное
          </label>

          {/* 3. Фильтр по частям речи (POS) */}
          <select
            value={pos}
            onChange={e => { setPos(e.target.value); setOffset(0); }}
            style={{
              padding: '8px 12px', background: '#16162a', border: '1px solid rgba(255,255,255,0.15)',
              borderRadius: 8, color: '#ffffff', fontSize: 13, outline: 'none',
            }}
          >
            <option value="" style={{ background: '#16162a', color: '#ffffff' }}>🏷️ Все части речи</option>
            <option value="noun" style={{ background: '#16162a', color: '#ffffff' }}>Существительные (noun)</option>
            <option value="verb" style={{ background: '#16162a', color: '#ffffff' }}>Глаголы (verb)</option>
            <option value="adjective" style={{ background: '#16162a', color: '#ffffff' }}>Прилагательные (adjective)</option>
            <option value="adverb" style={{ background: '#16162a', color: '#ffffff' }}>Наречия (adverb)</option>
            <option value="phrase" style={{ background: '#16162a', color: '#ffffff' }}>Фразы / Идиомы (phrase)</option>
            <option value="preposition" style={{ background: '#16162a', color: '#ffffff' }}>Предлоги (preposition)</option>
            <option value="pronoun" style={{ background: '#16162a', color: '#ffffff' }}>Местоимения (pronoun)</option>
          </select>

          {/* 6. Фильтры контроля качества (Health Control) */}
          <select
            value={health}
            onChange={e => { setHealth(e.target.value); setOffset(0); }}
            style={{
              padding: '8px 12px',
              background: health ? '#2d141e' : '#16162a',
              border: health ? '1px solid #f43f5e' : '1px solid rgba(255,255,255,0.15)',
              borderRadius: 8, color: '#ffffff', fontSize: 13, fontWeight: health ? 600 : 400, outline: 'none',
            }}
          >
            <option value="" style={{ background: '#16162a', color: '#ffffff' }}>🛡️ Контроль качества (Все слова)</option>
            <option value="few_examples" style={{ background: '#16162a', color: '#ffffff' }}>⚠️ Менее 3 примеров ({stats?.health?.few_examples || 0})</option>
            <option value="missing_phonetic" style={{ background: '#16162a', color: '#ffffff' }}>⚠️ Без транскрипции/фонетики ({stats?.health?.missing_phonetic || 0})</option>
            <option value="many_alts" style={{ background: '#16162a', color: '#ffffff' }}>⚠️ Много альтернатив (&gt; 3)</option>
          </select>

          {/* Альтернативы */}
          <select
            value={hasAlts}
            onChange={e => { setHasAlts(e.target.value); setOffset(0); }}
            style={{
              padding: '8px 12px', background: '#16162a', border: '1px solid rgba(255,255,255,0.15)',
              borderRadius: 8, color: '#ffffff', fontSize: 13, outline: 'none',
            }}
          >
            <option value="" style={{ background: '#16162a', color: '#ffffff' }}>Все слова</option>
            <option value="true" style={{ background: '#16162a', color: '#ffffff' }}>С альтернативами ({stats?.with_alts || 0})</option>
            <option value="false" style={{ background: '#16162a', color: '#ffffff' }}>Без альтернатив ({stats?.without_alts || 0})</option>
          </select>

          <div style={{ color: '#888', fontSize: 12, display: 'flex', alignItems: 'center', marginLeft: 4 }}>
            {total} слов
          </div>
        </div>
      </div>

      {/* Таблица — единственный скроллируемый контейнер */}
      <div
        className="dict-table-scroll"
        style={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          borderRadius: 10,
          border: '1px solid rgba(255,255,255,0.08)',
          background: '#10101f',
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04), 0 8px 32px rgba(0,0,0,0.3)',
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
          <thead>
            <tr>
              <th style={{ ...headerStyle, width: 40 }}>ID</th>
              <th style={headerStyle}>Слово</th>
              <th style={headerStyle}>Мн. число</th>
              <th style={headerStyle}>Перевод</th>
              <th style={headerStyle}>Альтернативы</th>
              <th style={{ ...headerStyle, width: 65 }}>CEFR</th>
              <th style={{ ...headerStyle, width: 75 }}>POS</th>
              <th style={headerStyle}>Транскр.</th>
              <th style={headerStyle}>Фонетика</th>
              <th style={headerStyle}>Ударение</th>
              <th style={{ ...headerStyle, minWidth: 320 }}>Примеры</th>
              <th style={{ ...headerStyle, width: 70 }}></th>
            </tr>
          </thead>
          <tbody>
            {words.map(w => {
              const cefrCfg = CEFR_CONFIG[w.cefr_level] || { color: '#888', bg: 'transparent', border: 'transparent' };
              const isSelected = selectedWord?.id === w.id;
              return (
                <tr
                  key={w.id}
                  onClick={() => setSelectedWord(w)}
                  style={{
                    background: isSelected ? 'rgba(139,92,246,0.15)' : 'transparent',
                    cursor: 'pointer',
                    transition: 'background 0.1s ease',
                  }}
                  onMouseEnter={e => { if (!isSelected) (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.02)'; }}
                  onMouseLeave={e => { if (!isSelected) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                >
                  <td style={{ ...cellStyle, color: '#555', fontSize: 11 }}>{w.id}</td>
                  <td style={cellStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ color: '#c084fc', fontWeight: 700, fontSize: 14 }}>{w.original_text}</span>
                      <button
                        onClick={e => { e.stopPropagation(); speakWord(w.original_text); }}
                        title="Прослушать произношение"
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, padding: '2px', opacity: 0.6,
                        }}
                      >
                        🔊
                      </button>
                    </div>
                  </td>
                  <td style={cellStyle}>
                    <span style={{ color: '#38bdf8' }}>{w.plural_form || '—'}</span>
                  </td>
                  <td style={cellStyle}>
                    <span style={{ fontWeight: 600 }}>{w.main_translation_text}</span>
                  </td>
                  <td style={{ ...cellStyle, background: (w.translations || []).length > 3 ? 'rgba(255,193,7,0.08)' : undefined }}>
                    <span style={{ color: '#aaa', fontSize: 12 }}>
                      {(w.translations || []).map((t: any) => t.translation_text).join(', ') || '—'}
                    </span>
                  </td>
                  <td style={cellStyle}>
                    <span style={{
                      padding: '2px 6px', borderRadius: 4, fontSize: 11, fontWeight: 700,
                      background: cefrCfg.bg, color: cefrCfg.color, border: `1px solid ${cefrCfg.border}`
                    }}>
                      {w.cefr_level || '—'}
                    </span>
                  </td>
                  <td style={cellStyle}>
                    <span style={{ fontSize: 11, color: '#94a3b8', background: 'rgba(255,255,255,0.05)', padding: '2px 5px', borderRadius: 4 }}>
                      {w.part_of_speech || '—'}
                    </span>
                  </td>
                  <td style={{ ...cellStyle, fontSize: 12, color: '#94a3b8' }}>
                    {w.transcription || '—'}
                  </td>
                  <td style={{ ...cellStyle, fontSize: 12, color: '#94a3b8' }}>
                    {w.russian_phonetic || '—'}
                  </td>
                  <td style={{ ...cellStyle, fontSize: 12 }}>
                    <div style={{ color: '#c084fc', fontWeight: 500, fontSize: 13 }}>
                      {withStress(w.original_text, w.transcription || '', EN_VOWELS)}
                    </div>
                    <div style={{ color: '#888', fontSize: 11 }}>
                      {withStress(w.russian_phonetic || '', w.transcription || '', RU_VOWELS)}
                    </div>
                  </td>
                  <td style={{ ...cellStyle, fontSize: 12 }}>
                    {(w.examples || []).map((ex: any) => (
                      <div key={ex.id} style={{ marginBottom: 6, padding: '3px 0', borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
                        <div style={{ color: '#e2e8f0', marginBottom: 2 }}>
                          <HighlightedWordSentence sentence={ex.sentence_en} targetWord={w.original_text} />
                        </div>
                        <div style={{ color: '#94a3b8' }}>
                          <HighlightedWordSentence sentence={ex.sentence_ru} targetWord={w.main_translation_text} />
                        </div>
                      </div>
                    ))}
                  </td>
                  <td style={cellStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <button
                        onClick={e => { e.stopPropagation(); setSelectedWord(w); }}
                        style={{
                          padding: '4px 8px', background: 'rgba(139,92,246,0.15)',
                          border: '1px solid rgba(139,92,246,0.3)', borderRadius: 6,
                          color: '#c084fc', fontSize: 11, cursor: 'pointer', fontWeight: 600,
                        }}
                      >
                        ✏️
                      </button>
                      <button
                        onClick={e => deleteWord(w.id, w.original_text, e)}
                        style={{ background: 'none', border: 'none', color: '#ff5252', cursor: 'pointer', fontSize: 16 }}
                        title="Удалить"
                      >
                        ×
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Пагинация */}
      <div style={{
        flexShrink: 0,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: 10,
        padding: '8px 16px',
        background: '#121224',
        borderRadius: 10,
        border: '1px solid rgba(255,255,255,0.08)',
        boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
      }}>
        <div style={{ color: '#94a3b8', fontSize: 13 }}>
          Показано <b style={{ color: '#fff' }}>{words.length}</b> из <b style={{ color: '#c084fc' }}>{total}</b> слов
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - limit))}
            style={{ padding: '6px 16px', background: '#1a1a2e', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, color: '#e2e8f0', fontSize: 13, cursor: offset === 0 ? 'default' : 'pointer', opacity: offset === 0 ? 0.4 : 1, transition: 'all 0.15s ease' }}
          >
            ← Назад
          </button>
          <span style={{ color: '#c084fc', fontWeight: 700, fontSize: 13, padding: '0 8px' }}>
            {page} / {totalPages || 1}
          </span>
          <button
            disabled={offset + limit >= total}
            onClick={() => setOffset(offset + limit)}
            style={{ padding: '6px 16px', background: '#1a1a2e', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, color: '#e2e8f0', fontSize: 13, cursor: offset + limit >= total ? 'default' : 'pointer', opacity: offset + limit >= total ? 0.4 : 1, transition: 'all 0.15s ease' }}
          >
            Вперёд →
          </button>
        </div>
      </div>

      {/* Боковая шторка (Drawer) */}
      {selectedWord && (
        <WordDrawer
          word={selectedWord}
          onClose={() => setSelectedWord(null)}
          onSaved={() => {
            setSelectedWord(null);
            load();
            loadStats();
          }}
        />
      )}
    </div>
  );
}

// ===== Главная страница =====
export function AdminDictPage() {
  const [authed, setAuthed] = useState(!!localStorage.getItem('admin_token'));

  useEffect(() => {
    if (!authed) return;
    adminApi('/dictionary/words?limit=1').catch(() => {
      localStorage.removeItem('admin_token');
      setAuthed(false);
    });
  }, [authed]);

  if (!authed) return <LoginScreen onLogin={() => setAuthed(true)} />;

  return (
    <div style={{
      height: '100vh',
      width: '100vw',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      background: '#0c0c18',
      color: '#fff',
      boxSizing: 'border-box',
    }}>
      {/* Шапка страницы */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '12px 24px',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
        background: '#0f0f1a',
        flexShrink: 0,
      }}>
        <h1 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>📖 2EnBota Admin — Словарь</h1>
        <button
          onClick={() => { localStorage.removeItem('admin_token'); setAuthed(false); }}
          style={{ padding: '6px 14px', background: 'rgba(255,82,82,0.1)', border: '1px solid rgba(255,82,82,0.3)', borderRadius: 8, color: '#ff5252', fontSize: 12, cursor: 'pointer' }}
        >
          Выйти
        </button>
      </div>

      {/* Основная рабочая область — занимает 100% оставшейся высоты без скролла окна */}
      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: '14px 24px 16px 24px' }}>
        <DictionaryEditor />
      </div>
    </div>
  );
}
